// Incremental parser for the build-deck assistant stream. Re-runs on
// every delta (buffer is small, <20KB) and returns the current parsed
// deck (once the Arena fences close) plus per-section status + body.
//
// The v6 prompt pins the final-answer order so the deck is available
// before any analysis section starts streaming — StreamingDeckResult
// renders that as "decklist now, section tiles with loading indicators
// that fill in as text arrives."

import type { Deck, DeckEntry } from "./api";

export type SectionKey =
  | "winCondition"
  | "keyInteractions"
  | "manaBase"
  | "sideboardGuide"
  | "weaknesses"
  | "exemplarsConsulted"
  | "honorableMentions";

// Tile order in the streaming result grid. `exemplarsConsulted` lives
// at the very bottom because (a) it's conditional — only appears when
// the exemplars toggle was on and the agent actually called
// get_archetype_exemplars — and (b) putting it last means if it never
// streams in, the empty space is at the visual tail rather than a hole
// in the middle of the grid.
export const SECTION_ORDER: SectionKey[] = [
  "winCondition",
  "keyInteractions",
  "manaBase",
  "sideboardGuide",
  "weaknesses",
  "honorableMentions",
  "exemplarsConsulted",
];

export type SectionStatus = "pending" | "streaming" | "done";

export interface ProgressiveState {
  /** Parsed decklist once both Arena fences have closed. null while still
   *  streaming the fences; also null on builds that haven't started the
   *  final answer yet. */
  deck: Deck | null;
  /** Per-section body text and status. Keys always in SECTION_ORDER. */
  sections: Record<SectionKey, { status: SectionStatus; body: string }>;
  /** True once the buffer contains the closing Deck fence. Lets the UI
   *  transition from "thinking" state to "here's the deck + tiles". */
  hasDeck: boolean;
}

const ARENA_DECK_RE = /```[^\r\n]*\r?\n\s*Deck\s*\r?\n([\s\S]*?)\r?\n```/;
const ARENA_SIDE_RE = /```[^\r\n]*\r?\n\s*Sideboard\s*\r?\n([\s\S]*?)\r?\n```/;
// Entry lines: "4 Monstrous Rage (DSK) 141" — set + collector optional.
const ENTRY_RE = /^\s*(\d+)\s+(.+?)(?:\s+\(([A-Za-z0-9]+)\)\s+(\S+))?\s*$/;

function parseEntries(block: string): DeckEntry[] {
  const out: DeckEntry[] = [];
  for (const rawLine of block.split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;
    const m = ENTRY_RE.exec(line);
    if (!m) continue;
    const entry: DeckEntry = {
      count: Number.parseInt(m[1]!, 10),
      name: m[2]!.trim(),
    };
    if (m[3]) entry.set = m[3].toLowerCase();
    if (m[4]) entry.collector_number = m[4];
    out.push(entry);
  }
  return out;
}

function canonicalKey(title: string): SectionKey | null {
  const t = title.toLowerCase().replace(/[^a-z]/g, "");
  if (/^wincondition/.test(t)) return "winCondition";
  if (/^(keyinteractions?|keysynergies?|keyline|synergies|gameplan)/.test(t))
    return "keyInteractions";
  if (/^mana(base|curve)/.test(t)) return "manaBase";
  if (/^sideboard(guide|ing|plan)/.test(t)) return "sideboardGuide";
  if (/^(real)?weakness/.test(t)) return "weaknesses";
  if (/^exemplarsconsulted/.test(t)) return "exemplarsConsulted";
  if (/^honorablemention/.test(t)) return "honorableMentions";
  return null;
}

export function parseProgressive(
  buffer: string,
  seedDeck: Deck | null = null,
  complete = false,
): ProgressiveState {
  const sections: ProgressiveState["sections"] = {
    winCondition: { status: "pending", body: "" },
    keyInteractions: { status: "pending", body: "" },
    manaBase: { status: "pending", body: "" },
    sideboardGuide: { status: "pending", body: "" },
    weaknesses: { status: "pending", body: "" },
    exemplarsConsulted: { status: "pending", body: "" },
    honorableMentions: { status: "pending", body: "" },
  };

  const deckMatch = ARENA_DECK_RE.exec(buffer);
  const sideMatch = ARENA_SIDE_RE.exec(buffer);
  let deck: Deck | null = seedDeck;
  if (deckMatch) {
    deck = {
      format: "standard",
      mainboard: parseEntries(deckMatch[1]!),
      sideboard: sideMatch ? parseEntries(sideMatch[1]!) : [],
    };
  }

  // Strip fenced blocks so Arena headings don't get confused with H2s.
  const noCode = buffer.replace(/```[\s\S]*?```/g, "");
  const lines = noCode.split("\n");

  interface Boundary {
    key: SectionKey;
    lineIdx: number;
  }
  const bounds: Boundary[] = [];
  for (let i = 0; i < lines.length; i++) {
    const L = lines[i]!;
    const m = /^##\s+(.+?)\s*$/.exec(L);
    if (!m) continue;
    const key = canonicalKey(m[1]!);
    if (!key) continue;
    // Keep only the FIRST occurrence of each canonical key so duplicate
    // headers (unlikely but cheap to defend against) don't overwrite
    // already-streamed content.
    if (bounds.some((b) => b.key === key)) continue;
    bounds.push({ key, lineIdx: i });
  }

  for (let k = 0; k < bounds.length; k++) {
    const b = bounds[k]!;
    const endLine = k + 1 < bounds.length ? bounds[k + 1]!.lineIdx : lines.length;
    const body = lines.slice(b.lineIdx + 1, endLine).join("\n").trim();
    // A section is `done` once the next heading has started or the caller
    // confirms the complete answer; otherwise tokens may still reach its tail.
    const isLast = k === bounds.length - 1;
    sections[b.key] = {
      status: isLast && !complete ? "streaming" : "done",
      body,
    };
  }

  return {
    deck,
    sections,
    hasDeck: !!deck,
  };
}
